import { useState, useEffect } from 'react'
import { Sliders, Play, Sparkles, Layers } from 'lucide-react'
import { createJob, getEstimate, type EstimateResponse, type UploadResponse } from '../lib/api'
import { formatDuration, formatCurrency, MODEL_INFO, MERGE_STRATEGIES } from '../lib/utils'
import { TranscriptionSettings, type TranscriptionConfig, DEFAULT_TRANSCRIPTION_CONFIG } from './TranscriptionSettings'

interface JobConfigProps {
  uploadedFiles: UploadResponse[]
  onJobCreated: (jobId: string) => void
}

export function JobConfig({ uploadedFiles, onJobCreated }: JobConfigProps) {
  const [name, setName] = useState('')
  const [model, setModel] = useState('htdemucs_ft')

  // Ensemble settings
  const [ensembleModels, setEnsembleModels] = useState<string[]>([])
  const [mergeStrategy, setMergeStrategy] = useState<string>('snr_weighted')

  // Transcription settings (comprehensive)
  const [transcriptionConfig, setTranscriptionConfig] = useState<TranscriptionConfig>(DEFAULT_TRANSCRIPTION_CONFIG)

  const [estimate, setEstimate] = useState<EstimateResponse | null>(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isEnsemble = model === 'ensemble'
  const availableModelsForEnsemble = Object.keys(MODEL_INFO).filter(m => m !== 'ensemble')

  useEffect(() => {
    if (uploadedFiles.length === 0) {
      setEstimate(null)
      return
    }

    const fetchEstimate = async () => {
      try {
        const data = await getEstimate(
          uploadedFiles.length,
          model,
          1, // Always 1 GPU
          'ultra', // Always ULTRA quality
          isEnsemble ? ensembleModels : undefined
        )
        setEstimate(data)
      } catch {
        console.error('Failed to get estimate')
      }
    }

    fetchEstimate()
  }, [uploadedFiles.length, model, isEnsemble, ensembleModels])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      setError('Please enter a job name')
      return
    }

    if (uploadedFiles.length === 0) {
      setError('Please upload files first')
      return
    }

    if (isEnsemble && ensembleModels.length < 2) {
      setError('Please select at least 2 models for ensemble')
      return
    }

    setCreating(true)
    setError(null)

    try {
      const config: Record<string, unknown> = {
        model,
        gpus: 1,
        max_retries: 3,
        quality_preset: 'ultra',
        transcription: transcriptionConfig,
        enable_transcription: true,
      }

      if (isEnsemble) {
        config.ensemble_settings = {
          models: ensembleModels,
          merge_strategy: mergeStrategy
        }
      }

      const job = await createJob(name, config, uploadedFiles.map(f => f.filename))

      onJobCreated(job.job_id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create job')
    } finally {
      setCreating(false)
    }
  }

  const handleEnsembleModelToggle = (modelId: string) => {
    if (ensembleModels.includes(modelId)) {
      setEnsembleModels(ensembleModels.filter(m => m !== modelId))
    } else {
      setEnsembleModels([...ensembleModels, modelId])
    }
  }

  const selectedModelInfo = MODEL_INFO[model]

  return (
    <form onSubmit={handleSubmit}>
      <h2 className="section-title mb-4">
        <Sliders className="w-5 h-5 text-primary-400" />
        Configure Job
      </h2>

      <div className="space-y-4">
        <div>
          <label className="label">Job Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My Separation Job"
            className="input w-full"
          />
        </div>

        <div>
          <label className="label">Model</label>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="input w-full"
          >
            <optgroup label="4-Stem Separation">
              {Object.entries(MODEL_INFO)
                .filter(([_, info]) => info.framework === 'demucs' && info.stems >= 4)
                .sort((a, b) => (b[1].sdr || 0) - (a[1].sdr || 0))
                .map(([key, info]) => (
                  <option key={key} value={key}>
                    {info.description} {info.sdr ? `(SDR: ${info.sdr})` : ''} {key === 'htdemucs_ft' ? '★' : ''}
                  </option>
                ))}
            </optgroup>
            <optgroup label="Vocal Extraction (2 stems)">
              {Object.entries(MODEL_INFO)
                .filter(([_, info]) => info.framework === 'audio_separator')
                .sort((a, b) => (b[1].sdr || 0) - (a[1].sdr || 0))
                .map(([key, info]) => (
                  <option key={key} value={key}>
                    {info.description} {info.sdr ? `(SDR: ${info.sdr})` : ''}
                  </option>
                ))}
            </optgroup>
            <optgroup label="Maximum Quality">
              <option value="ensemble">Ensemble - Combine multiple models (SDR: 10+)</option>
            </optgroup>
          </select>
          {selectedModelInfo && (
            <p className="text-xs text-gray-500 mt-1">
              {selectedModelInfo.stems} stems | {selectedModelInfo.bestFor}
              {selectedModelInfo.memoryGb && ` | ${selectedModelInfo.memoryGb} GB VRAM`}
            </p>
          )}
        </div>

        {/* Ensemble Model Selection */}
        {isEnsemble && (
          <div className="p-3 rounded-xl bg-surface-800/50 border border-primary-500/20">
            <label className="label flex items-center gap-2 mb-2">
              <Layers className="w-4 h-4 text-primary-400" />
              Select Models for Ensemble
            </label>
            <div className="grid grid-cols-2 gap-2">
              {availableModelsForEnsemble.map(modelId => (
                <label
                  key={modelId}
                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                    ensembleModels.includes(modelId)
                      ? 'bg-primary-500/20 border border-primary-500/50'
                      : 'bg-surface-700/50 border border-white/5 hover:border-white/10'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={ensembleModels.includes(modelId)}
                    onChange={() => handleEnsembleModelToggle(modelId)}
                    className="rounded border-gray-600"
                  />
                  <span className="text-sm">{MODEL_INFO[modelId]?.description.split(' ')[0]}</span>
                </label>
              ))}
            </div>
            {ensembleModels.length > 0 && (
              <p className="text-xs text-gray-400 mt-2">
                Selected: {ensembleModels.length} models
              </p>
            )}

            <div className="mt-3">
              <label className="label text-xs">Merge Strategy</label>
              <select
                value={mergeStrategy}
                onChange={(e) => setMergeStrategy(e.target.value)}
                className="input w-full text-sm"
              >
                {MERGE_STRATEGIES.map(strategy => (
                  <option key={strategy.id} value={strategy.id}>
                    {strategy.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Comprehensive MIDI Transcription Settings */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="label flex items-center gap-2 m-0">
              <Sparkles className="w-4 h-4 text-purple-400" />
              MIDI Transcription Settings
            </h3>
            <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full border border-purple-500/30">
              40+ Parameters
            </span>
          </div>
          <TranscriptionSettings
            value={transcriptionConfig}
            onChange={setTranscriptionConfig}
          />
        </div>

        {/* ULTRA Quality Badge - Lambda always uses ULTRA */}
        <div className="bg-purple-500/20 border border-purple-500/50 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-purple-300 font-medium">ULTRA Quality</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            10 shifts, 75% overlap, 24-bit output
          </p>
        </div>
      </div>

      {estimate && uploadedFiles.length > 0 && (
        <div className="mt-4 p-3 rounded-xl bg-surface-800/50 border border-white/5">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Files</span>
              <span className="text-white font-medium">{uploadedFiles.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Est. Time</span>
              <span className="text-white font-medium">
                {formatDuration(estimate.estimated_time_hours * 3600)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Est. Cost</span>
              <span className="text-amber-400 font-medium">
                {formatCurrency(estimate.estimated_cost_usd)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Speed</span>
              <span className="text-white font-medium">
                {estimate.throughput_samples_per_min.toFixed(0)}/min
              </span>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={creating || uploadedFiles.length === 0}
        className="btn-primary w-full mt-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {creating ? (
          <>
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Creating...
          </>
        ) : (
          <>
            <Play className="w-4 h-4" />
            Start Separation
          </>
        )}
      </button>
    </form>
  )
}
